import { useState, useEffect } from 'react';
import { D, st, toArr, apiGet, isDeductible} from '../constants';
import { StatGrid } from './shared';

export default function Reports({invoices, clientName, calcTotal, fmt, fmtDate, expenses, deposits, recurring, companyInfo, me}) {
  const isTrial = me?.company?.plan === 'trial';
  const [bidAccuracy, setBidAccuracy] = useState(null);
  useEffect(() => { apiGet('/data/bid-accuracy').then(setBidAccuracy).catch(()=>setBidAccuracy({jobs:[],calibration:null})); }, []);
  const companyName = companyInfo?.name;
  const companyLic = companyInfo?.licenseNum;

  const [employees, setEmployees] = useState([]);
  const [payrollEntries, setPayrollEntries] = useState([]);
  useEffect(() => {
    apiGet('/data/users').then(rows=>setEmployees(toArr(rows))).catch(()=>{});
    apiGet('/data/payroll').then(rows=>setPayrollEntries(toArr(rows))).catch(()=>{});
  }, []);

  const curYear = new Date().getFullYear();
  const [year, setYear] = useState(curYear);
  const [quarter, setQuarter] = useState("annual");
  const [reportType, setReportType] = useState("pl");

  const quarters = {Q1:[1,3], Q2:[4,6], Q3:[7,9], Q4:[10,12], annual:[1,12]};
  const [mStart, mEnd] = quarters[quarter];
  const inPeriod = (dateStr) => {
    if (!dateStr) return false;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return false;
      const m = d.getMonth()+1;
      return d.getFullYear()===year && m>=mStart && m<=mEnd;
    } catch(_) { return false; }
  };
  const periodLabel = quarter==="annual" ? `FY ${year}` : `${quarter} ${year}`;

  const paidInvoices = toArr(invoices).filter(i=>i.status==="Paid" && inPeriod(i.paidDate||i.date));
  const revenue = paidInvoices.reduce((s,i)=>s+calcTotal(i.items),0);
  const allInvoices = toArr(invoices).filter(i=>inPeriod(i.date));
  const outstanding = toArr(invoices).filter(i=>i.status!=="Paid" && inPeriod(i.date)).reduce((s,i)=>s+calcTotal(i.items),0);
  const depositsInPeriod = toArr(deposits).filter(d=>inPeriod(d.date)).reduce((s,d)=>s+(Number(d.amount)||0),0);

  // Personal lines are excluded from EVERY figure on this page. They flow
  // through totalExp -> totalCOGS -> netProfit -> estimatedTaxableIncome, so
  // leaving them in would understate the tax owed - the dangerous direction.
  const expInPeriod = toArr(expenses).filter(e=>inPeriod(e.date) && isDeductible(e));
  const totalExp = expInPeriod.reduce((s,e)=>s+(Number(e.amount)||0),0);
  const expByCategory = {};
  expInPeriod.forEach(e => { const cat=e.category||"Other"; expByCategory[cat]=(expByCategory[cat]||0)+(Number(e.amount)||0); });

  const payEntries = payrollEntries.filter(e=>inPeriod(e.date));
  const totalPayroll = payEntries.reduce((s,e) => {
    const h = Number(e.hours)||0, r = Number(e.rate)||0;
    return s + (e.pay_type==="Hourly"||e.pay_type==="Overtime" ? h*r : r);
  }, 0);
  const payByEmp = {};
  payEntries.forEach(e => {
    const n = employees.find(x=>x.id===e.user_id)?.name || "Unknown";
    if (!payByEmp[n]) payByEmp[n] = {hours:0, gross:0, entries:0};
    const h = Number(e.hours)||0, r = Number(e.rate)||0;
    const amt = e.pay_type==="Hourly"||e.pay_type==="Overtime" ? h*r : r;
    payByEmp[n].hours += (e.pay_type==="Hourly"||e.pay_type==="Overtime" ? h : 0);
    payByEmp[n].gross += amt; payByEmp[n].entries++;
  });

  const totalCOGS = totalExp+totalPayroll;
  const grossProfit = revenue-totalCOGS;
  const recurringCosts = toArr(recurring).filter(r=>r && r.active).reduce((s,r) => {
    const monthsInPeriod = mEnd-mStart+1;
    const amt = Number(r.amount)||0;
    if (!r.frequency) return s+amt*monthsInPeriod;
    if (r.frequency==="Monthly") return s+amt*monthsInPeriod;
    if (r.frequency==="Annual"||r.frequency==="Annually") return s+(quarter==="annual"?amt:amt*(monthsInPeriod/12));
    return s+amt*monthsInPeriod;
  }, 0);
  const netProfit = grossProfit-recurringCosts;
  const profitMargin = revenue>0 ? ((netProfit/revenue)*100).toFixed(1) : 0;

  const months = [];
  for (let m=mStart; m<=mEnd; m++) {
    const label = new Date(year,m-1,1).toLocaleString("en-US",{month:"short"});
    const mRev = toArr(invoices).filter(i=>{ if(i.status!=="Paid") return false; const d=new Date(i.paidDate||i.date); return d.getFullYear()===year && d.getMonth()+1===m; }).reduce((s,i)=>s+calcTotal(i.items),0);
    const mExp = toArr(expenses).filter(e=>{ if(!isDeductible(e)) return false; const d=new Date(e.date); return d.getFullYear()===year && d.getMonth()+1===m; }).reduce((s,e)=>s+(Number(e.amount)||0),0);
    months.push({label, revenue:mRev, expenses:mExp, profit:mRev-mExp});
  }

  const today = new Date();
  const aging = {current:0, d30:0, d60:0, d90:0, over90:0};
  const agingRows = [];
  toArr(invoices).filter(i=>i.status!=="Paid" && i.status!=="Cancelled").forEach(i => {
    const due = new Date(i.dueDate||i.date);
    const days = Math.floor((today-due)/(1000*60*60*24));
    const amt = calcTotal(i.items);
    const bucket = days<=0?"current":days<=30?"d30":days<=60?"d60":days<=90?"d90":"over90";
    aging[bucket] += amt;
    agingRows.push({...i, daysOverdue:Math.max(0,days), amount:amt, bucket});
  });

  const SE_TAX_RATE = 0.1530;
  const FED_RATE = 0.22;
  const estimatedTaxableIncome = netProfit;
  const seTax = Math.max(0, estimatedTaxableIncome*0.9235)*SE_TAX_RATE;
  const fedTax = Math.max(0, estimatedTaxableIncome-seTax/2)*FED_RATE;
  const totalEstTax = seTax+fedTax;
  const quarterlyPayment = totalEstTax/4;

  const printReport = () => {
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
    const style = `
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Helvetica Neue',Arial,sans-serif;font-size:11px;color:#1a1a1a;background:#fff;padding:32px}
        h1{font-size:22px;font-weight:700;margin-bottom:4px}
        h2{font-size:14px;font-weight:700;margin:20px 0 10px;color:#1a1a1a;border-bottom:2px solid #f5c842;padding-bottom:4px}
        .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:16px;border-bottom:3px solid #1a1a1a}
        .logo{font-size:20px;font-weight:800;color:#1a1a1a}
        .logo span{color:#c9a020}
        .meta{text-align:right;font-size:10px;color:#666;line-height:1.6}
        .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px}
        .stat{background:#f8f8f6;border:1px solid #e0e0e0;border-radius:6px;padding:10px 12px}
        .stat-label{font-size:9px;text-transform:uppercase;letter-spacing:0.06em;color:#888;margin-bottom:4px}
        .stat-val{font-size:16px;font-weight:700;color:#1a1a1a}
        .stat-val.green{color:#1a7a4a}
        .stat-val.red{color:#c0392b}
        table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:10.5px}
        th{background:#1a1a1a;color:#fff;padding:6px 8px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:0.06em;font-weight:600}
        td{padding:5px 8px;border-bottom:1px solid #ebebeb;vertical-align:top}
        tr:nth-child(even) td{background:#fafaf8}
        .right{text-align:right}
        .bold{font-weight:700}
        .green{color:#1a7a4a}.red{color:#c0392b}.yellow{color:#c9a020}.gray{color:#888}
        .section-total{background:#f0f0ee!important;font-weight:700}
        .disclaimer{margin-top:24px;padding:10px 14px;background:#fffbea;border:1px solid #f5c842;border-radius:4px;font-size:9px;color:#666;line-height:1.6}
        .footer-print{margin-top:20px;text-align:center;font-size:9px;color:#aaa;border-top:1px solid #e0e0e0;padding-top:10px}
        .pl-row{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #ebebeb;font-size:11px}
        .pl-row.total{font-weight:700;font-size:12px;border-top:2px solid #1a1a1a;border-bottom:none;margin-top:4px;padding-top:8px}
        .pl-row.sub{color:#555;padding-left:16px}
        .pl-row.section{font-weight:600;padding-top:12px;color:#1a1a1a}
        @media print{body{padding:16px}}
      </style>
    `;

    const monthTable = `
      <h2>Monthly Revenue vs Expenses</h2>
      <table>
        <thead><tr><th>Month</th><th class="right">Revenue</th><th class="right">Expenses</th><th class="right">Net</th></tr></thead>
        <tbody>
          ${months.map(m=>`<tr><td>${m.label}</td><td class="right green">${fmt(m.revenue)}</td><td class="right red">${fmt(m.expenses)}</td><td class="right ${m.profit>=0?'green':'red'} bold">${fmt(m.profit)}</td></tr>`).join("")}
          <tr class="section-total"><td>Total</td><td class="right green">${fmt(revenue)}</td><td class="right red">${fmt(totalExp)}</td><td class="right ${netProfit>=0?'green':'red'}">${fmt(netProfit)}</td></tr>
        </tbody>
      </table>
    `;

    const plSection = `
      <h2>Profit & Loss Statement</h2>
      <div class="pl-row section"><span>REVENUE</span></div>
      <div class="pl-row sub"><span>Service Revenue (Invoices Paid)</span><span class="green">${fmt(revenue)}</span></div>
      <div class="pl-row sub"><span>Deposits Collected</span><span class="green">${fmt(depositsInPeriod)}</span></div>
      <div class="pl-row total"><span>GROSS REVENUE</span><span class="green">${fmt(revenue+depositsInPeriod)}</span></div>
      <div class="pl-row section" style="margin-top:12px"><span>COST OF GOODS SOLD</span></div>
      <div class="pl-row sub"><span>Materials & Supplies</span><span class="red">(${fmt(expByCategory["Materials"]||0)})</span></div>
      <div class="pl-row sub"><span>Subcontractors</span><span class="red">(${fmt(expByCategory["Subcontractor"]||0)})</span></div>
      <div class="pl-row sub"><span>Equipment & Tools</span><span class="red">(${fmt((expByCategory["Equipment"]||0)+(expByCategory["Tools"]||0))})</span></div>
      <div class="pl-row sub"><span>Fuel & Vehicle</span><span class="red">(${fmt(expByCategory["Fuel"]||0)})</span></div>
      <div class="pl-row sub"><span>Other Job Costs</span><span class="red">(${fmt(expByCategory["Other"]||0)})</span></div>
      <div class="pl-row sub"><span>Labor / Payroll</span><span class="red">(${fmt(totalPayroll)})</span></div>
      <div class="pl-row total"><span>TOTAL COGS</span><span class="red">(${fmt(totalCOGS)})</span></div>
      <div class="pl-row section" style="margin-top:12px;font-size:13px"><span>GROSS PROFIT</span><span class="${grossProfit>=0?'green':'red'}">${fmt(grossProfit)}</span></div>
      <div class="pl-row section" style="margin-top:12px"><span>OPERATING EXPENSES</span></div>
      <div class="pl-row sub"><span>Recurring / Overhead</span><span class="red">(${fmt(recurringCosts)})</span></div>
      <div class="pl-row total" style="font-size:13px"><span>NET PROFIT / LOSS</span><span class="${netProfit>=0?'green':'red'}">${fmt(netProfit)} (${profitMargin}% margin)</span></div>
    `;

    const expTable = `
      <h2>Expense Detail</h2>
      <table>
        <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Vendor</th><th class="right">Amount</th></tr></thead>
        <tbody>
          ${expInPeriod.slice().sort((a,b)=>(a.date||"").localeCompare(b.date||"")).map(e=>`<tr><td>${fmtDate(e.date)}</td><td>${esc(e.category||"Other")}</td><td>${esc(e.description||"—")}</td><td class="gray">${esc(e.vendor||"—")}</td><td class="right red">${fmt(Number(e.amount)||0)}</td></tr>`).join("")}
          <tr class="section-total"><td colspan="4">Total Expenses</td><td class="right red">${fmt(totalExp)}</td></tr>
        </tbody>
      </table>
    `;

    const payrollTable = `
      <h2>Payroll Summary</h2>
      <table>
        <thead><tr><th>Employee</th><th class="right">Hours</th><th class="right">Gross Pay</th><th class="right">Entries</th></tr></thead>
        <tbody>
          ${Object.entries(payByEmp).map(([n,d])=>`<tr><td class="bold">${esc(n)}</td><td class="right">${d.hours.toFixed(2)}h</td><td class="right bold">${fmt(d.gross)}</td><td class="right gray">${d.entries}</td></tr>`).join("")}
          <tr class="section-total"><td>Total Payroll</td><td></td><td class="right">${fmt(totalPayroll)}</td><td></td></tr>
        </tbody>
      </table>
    `;

    const arTable = `
      <h2>Accounts Receivable Aging</h2>
      <table>
        <thead><tr><th>Invoice</th><th>Client</th><th>Due Date</th><th class="right">Days Overdue</th><th class="right">Amount</th><th>Status</th></tr></thead>
        <tbody>
          ${agingRows.slice().sort((a,b)=>b.daysOverdue-a.daysOverdue).map(i=>`<tr><td>${esc(i.number||"—")}</td><td>${esc(clientName(i.clientId))}</td><td>${fmtDate(i.dueDate||i.date)}</td><td class="right ${i.daysOverdue>60?'red':i.daysOverdue>30?'yellow':''}">${i.daysOverdue>0?i.daysOverdue+" days":"Current"}</td><td class="right bold">${fmt(i.amount)}</td><td>${i.status}</td></tr>`).join("")}
          <tr class="section-total"><td colspan="4">Total Outstanding</td><td class="right">${fmt(outstanding)}</td><td></td></tr>
        </tbody>
      </table>
      <table style="margin-top:8px;max-width:400px">
        <thead><tr><th>Aging Bucket</th><th class="right">Amount</th></tr></thead>
        <tbody>
          <tr><td>Current (not yet due)</td><td class="right green bold">${fmt(aging.current)}</td></tr>
          <tr><td>1–30 days overdue</td><td class="right yellow bold">${fmt(aging.d30)}</td></tr>
          <tr><td>31–60 days overdue</td><td class="right bold" style="color:#e67e22">${fmt(aging.d60)}</td></tr>
          <tr><td>61–90 days overdue</td><td class="right red bold">${fmt(aging.d90)}</td></tr>
          <tr><td>90+ days overdue</td><td class="right red bold">${fmt(aging.over90)}</td></tr>
        </tbody>
      </table>
    `;

    const taxSection = `
      <h2>Estimated Tax Summary (Consult your CPA)</h2>
      <div class="pl-row"><span>Net Taxable Income</span><span>${fmt(estimatedTaxableIncome)}</span></div>
      <div class="pl-row"><span>Self-Employment Tax (15.3%)</span><span class="red">${fmt(seTax)}</span></div>
      <div class="pl-row"><span>Estimated Federal Income Tax (22%)</span><span class="red">${fmt(fedTax)}</span></div>
      <div class="pl-row total"><span>Total Estimated Tax Liability</span><span class="red">${fmt(totalEstTax)}</span></div>
      <div class="pl-row" style="margin-top:8px"><span>Estimated Quarterly Payment (÷4)</span><span class="yellow bold">${fmt(quarterlyPayment)}</span></div>
      <div class="disclaimer">⚠ These are rough estimates only based on simplified tax rates and are provided for planning purposes. They do not account for deductions, credits, filing status, state taxes, or other factors. Please consult a licensed CPA or tax professional before filing.</div>
    `;

    const invoiceTable = `
      <h2>Invoice Ledger</h2>
      <table>
        <thead><tr><th>Invoice #</th><th>Client</th><th>Date</th><th>Due</th><th class="right">Amount</th><th>Status</th></tr></thead>
        <tbody>
          ${allInvoices.slice().sort((a,b)=>(a.date||"").localeCompare(b.date||"")).map(i=>`<tr><td>${esc(i.number||"—")}</td><td>${esc(clientName(i.clientId))}</td><td>${fmtDate(i.date)}</td><td>${fmtDate(i.dueDate)}</td><td class="right bold">${fmt(calcTotal(i.items))}</td><td class="${i.status==="Paid"?"green":i.status==="Overdue"?"red":"gray"}">${i.status}</td></tr>`).join("")}
        </tbody>
      </table>
    `;

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>AmpBooks — ${periodLabel} Accounting Report</title>${style}</head><body>
      <div class="header">
        <div>
          <div class="logo">⚡ <span>AmpBooks</span> — ${esc(companyName||"Electrical Contractor")}</div>
          <div style="font-size:10px;color:#888;margin-top:4px">License: ${esc(companyLic||"")} &nbsp;·&nbsp; Generated: ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</div>
        </div>
        <div class="meta">
          <div style="font-size:16px;font-weight:700;color:#1a1a1a">${periodLabel} Financial Report</div>
          <div>Period: ${quarter==="annual"?`Jan 1 – Dec 31, ${year}`:`Q${quarter.charAt(1)} ${year} (${new Date(year,mStart-1,1).toLocaleString("en-US",{month:"long"})} – ${new Date(year,mEnd-1,1).toLocaleString("en-US",{month:"long"})})`}</div>
          <div style="margin-top:4px;font-weight:700;color:${netProfit>=0?"#1a7a4a":"#c0392b"}">Net Profit: ${fmt(netProfit)}</div>
        </div>
      </div>
      <div class="stats">
        <div class="stat"><div class="stat-label">Revenue</div><div class="stat-val green">${fmt(revenue)}</div></div>
        <div class="stat"><div class="stat-label">Total Expenses</div><div class="stat-val red">${fmt(totalExp+totalPayroll)}</div></div>
        <div class="stat"><div class="stat-label">Net Profit</div><div class="stat-val ${netProfit>=0?'green':'red'}">${fmt(netProfit)}</div></div>
        <div class="stat"><div class="stat-label">Profit Margin</div><div class="stat-val ${profitMargin>=0?'green':'red'}">${profitMargin}%</div></div>
      </div>
      ${plSection}
      ${monthTable}
      ${expTable}
      ${payrollTable}
      ${arTable}
      ${invoiceTable}
      ${taxSection}
      <div class="footer-print">AmpBooks · ${esc(companyName)} · ${periodLabel} Report · Confidential</div>
    </body></html>`;

    const win = window.open("", "_blank", "width=900,height=700");
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.print(); };
  };

  const ReportTab = ({id,label}) => (
    <button onClick={()=>setReportType(id)} style={{padding:"7px 16px",borderRadius:7,fontSize:12,fontWeight:reportType===id?600:400,cursor:"pointer",border:"none",background:reportType===id?D.accent:"transparent",color:reportType===id?"#fff":D.muted,whiteSpace:"nowrap"}}>
      {label}
    </button>
  );

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div style={st.h1}>📊 Reports & Accounting</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <select style={{...st.input,width:"auto",fontSize:12}} value={year} onChange={e=>setYear(parseInt(e.target.value))}>
            {[curYear-2,curYear-1,curYear,curYear+1].map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <select style={{...st.input,width:"auto",fontSize:12}} value={quarter} onChange={e=>setQuarter(e.target.value)}>
            <option value="annual">Full Year</option>
            <option value="Q1">Q1 (Jan–Mar)</option>
            <option value="Q2">Q2 (Apr–Jun)</option>
            <option value="Q3">Q3 (Jul–Sep)</option>
            <option value="Q4">Q4 (Oct–Dec)</option>
          </select>
          <button
            style={{...st.btn(isTrial?"":"primary"),marginLeft:0,display:"flex",alignItems:"center",gap:6,opacity:isTrial?0.7:1}}
            onClick={()=>{
              if (isTrial) { alert("Exporting reports to PDF is available on paid plans. Upgrade in Settings to unlock this feature."); return; }
              printReport();
            }}
          >{isTrial?"🔒":"🖨"} {isTrial?"Export PDF — Upgrade Required":"Print / Save PDF"}</button>
        </div>
      </div>

      <StatGrid stats={[
        {label:"Revenue", value:fmt(revenue), color:D.success},
        {label:"Total Expenses", value:fmt(totalExp+totalPayroll), color:D.danger},
        {label:"Net Profit", value:fmt(netProfit), color:netProfit>=0?D.success:D.danger},
        {label:"Profit Margin", value:profitMargin+"%", color:profitMargin>=20?D.success:profitMargin>=10?D.warning:D.danger},
      ]}/>
      <StatGrid stats={[
        {label:"Outstanding AR", value:fmt(outstanding), color:D.warning},
        {label:"Payroll", value:fmt(totalPayroll), color:D.muted},
        {label:"Deposits", value:fmt(depositsInPeriod), color:D.accent},
        {label:"Est. Tax Liability", value:fmt(totalEstTax), color:D.danger},
      ]}/>

      <div style={{display:"flex",gap:4,marginBottom:20,background:D.surface,padding:4,borderRadius:10,width:"fit-content",border:`1px solid ${D.border}`,flexWrap:"wrap"}}>
        <ReportTab id="pl" label="P&L"/>
        <ReportTab id="cashflow" label="Monthly"/>
        <ReportTab id="expenses" label="Expenses"/>
        <ReportTab id="payroll" label="Payroll"/>
        <ReportTab id="ar" label="AR Aging"/>
        <ReportTab id="tax" label="Tax Est."/>
        <ReportTab id="invoices" label="Invoice Ledger"/>
        <ReportTab id="bidaccuracy" label="Bid Accuracy"/>
      </div>

      {reportType==="pl" && (
        <div style={st.card}>
          <div style={{...st.h2,marginBottom:16}}>Profit & Loss — {periodLabel}</div>
          {[
            {label:"REVENUE",type:"section"},
            {label:"Service Revenue (Invoices Paid)",val:revenue,color:D.success,sub:true},
            {label:"Deposits Collected",val:depositsInPeriod,color:D.success,sub:true},
            {label:"GROSS REVENUE",val:revenue+depositsInPeriod,color:D.success,type:"total"},
            {label:"COST OF GOODS SOLD",type:"section"},
            {label:"Materials & Supplies",val:-(expByCategory["Materials"]||0),color:D.danger,sub:true},
            {label:"Subcontractors",val:-(expByCategory["Subcontractor"]||0),color:D.danger,sub:true},
            {label:"Equipment & Tools",val:-((expByCategory["Equipment"]||0)+(expByCategory["Tools"]||0)),color:D.danger,sub:true},
            {label:"Fuel & Vehicle",val:-(expByCategory["Fuel"]||0),color:D.danger,sub:true},
            {label:"Other Job Costs",val:-(expByCategory["Other"]||0),color:D.danger,sub:true},
            {label:"Labor / Payroll",val:-totalPayroll,color:D.danger,sub:true},
            {label:"TOTAL COGS",val:-totalCOGS,color:D.danger,type:"total"},
            {label:"GROSS PROFIT",val:grossProfit,color:grossProfit>=0?D.success:D.danger,type:"subtotal"},
            {label:"OPERATING EXPENSES",type:"section"},
            {label:"Recurring & Overhead",val:-recurringCosts,color:D.danger,sub:true},
            {label:"NET PROFIT / LOSS",val:netProfit,color:netProfit>=0?D.success:D.danger,type:"total",big:true},
          ].map((row,i) => {
            if (row.type==="section") return <div key={i} style={{fontSize:11,fontWeight:700,color:D.muted,letterSpacing:"0.08em",textTransform:"uppercase",padding:"14px 0 4px",borderTop:i>0?`1px solid ${D.border}`:"none"}}>{row.label}</div>;
            if (row.type==="total"||row.type==="subtotal") return (
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderTop:`2px solid ${D.border}`,marginTop:4}}>
                <span style={{fontWeight:700,fontSize:row.big?15:13,color:D.text}}>{row.label}</span>
                <span style={{fontWeight:700,fontSize:row.big?15:13,color:row.color}}>{row.val<0?`(${fmt(Math.abs(row.val))})`:fmt(row.val)}</span>
              </div>
            );
            return (
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",paddingLeft:row.sub?16:0,borderBottom:`1px solid ${D.border}22`}}>
                <span style={{fontSize:13,color:D.muted}}>{row.label}</span>
                <span style={{fontSize:13,color:row.color||D.text,fontWeight:500}}>{row.val<0?`(${fmt(Math.abs(row.val))})`:fmt(row.val)}</span>
              </div>
            );
          })}
          <div style={{marginTop:16,padding:"10px 14px",background:D.surface,borderRadius:8,fontSize:12,color:D.muted}}>
            Profit margin: <strong style={{color:profitMargin>=20?D.success:profitMargin>=10?D.warning:D.danger}}>{profitMargin}%</strong>
            &nbsp;·&nbsp; Invoices paid: <strong style={{color:D.text}}>{paidInvoices.length}</strong>
            &nbsp;·&nbsp; Outstanding: <strong style={{color:D.warning}}>{fmt(outstanding)}</strong>
          </div>
        </div>
      )}

      {reportType==="cashflow" && (
        <div style={st.card}>
          <div style={{...st.h2,marginBottom:16}}>Monthly Revenue vs Expenses — {year}</div>
          <div style={{overflowX:"auto"}}><table style={st.table}>
            <thead><tr>{["Month","Revenue","Expenses","Net","Margin"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
            <tbody>
              {months.map(m => (
                <tr key={m.label}>
                  <td style={{...st.td,fontWeight:500}}>{m.label} {year}</td>
                  <td style={{...st.td,color:D.success,fontWeight:500}}>{fmt(m.revenue)}</td>
                  <td style={{...st.td,color:D.danger}}>{fmt(m.expenses)}</td>
                  <td style={{...st.td,fontWeight:600,color:m.profit>=0?D.success:D.danger}}>{fmt(m.profit)}</td>
                  <td style={{...st.td,color:D.muted,fontSize:12}}>{m.revenue>0?((m.profit/m.revenue)*100).toFixed(1)+"%":"—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={{...st.td,fontWeight:700}}>Total</td>
              <td style={{...st.td,fontWeight:700,color:D.success}}>{fmt(revenue)}</td>
              <td style={{...st.td,fontWeight:700,color:D.danger}}>{fmt(totalExp)}</td>
              <td style={{...st.td,fontWeight:700,color:netProfit>=0?D.success:D.danger}}>{fmt(netProfit)}</td>
              <td style={{...st.td,color:D.muted}}>{profitMargin}%</td>
            </tr></tfoot>
          </table></div>
        </div>
      )}

      {reportType==="expenses" && (
        <>
          <div style={{...st.card,marginBottom:16}}>
            <div style={{...st.h2,marginBottom:12}}>Expenses by Category — {periodLabel}</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10,marginBottom:4}}>
              {Object.entries(expByCategory).sort((a,b)=>b[1]-a[1]).map(([cat,amt]) => (
                <div key={cat} style={{background:D.surface,border:`1px solid ${D.border}`,borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:11,color:D.muted,marginBottom:4}}>{cat}</div>
                  <div style={{fontSize:15,fontWeight:700,color:D.danger}}>{fmt(amt)}</div>
                  <div style={{fontSize:10,color:D.muted,marginTop:2}}>{revenue>0?((amt/revenue)*100).toFixed(1)+"% of revenue":""}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={st.card}>
            <div style={{...st.h2,marginBottom:12}}>Expense Detail</div>
            <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead><tr>{["Date","Category","Description","Vendor","Billable","Amount"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {expInPeriod.slice().sort((a,b)=>(a.date||"").localeCompare(b.date||"")).map(e => (
                  <tr key={e.id}>
                    <td style={st.td}>{fmtDate(e.date)}</td>
                    <td style={st.td}><span style={{background:D.surface,fontSize:11,padding:"2px 6px",borderRadius:4,color:D.accent}}>{e.category||"Other"}</span></td>
                    <td style={st.td}>{e.description||"—"}</td>
                    <td style={{...st.td,color:D.muted}}>{e.vendor||"—"}</td>
                    <td style={st.td}><span style={{fontSize:11,color:e.billable!==false?D.success:D.muted}}>{e.billable!==false?"Yes":"No"}</span></td>
                    <td style={{...st.td,color:D.danger,fontWeight:500}}>{fmt(Number(e.amount)||0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td colSpan={5} style={{...st.td,fontWeight:700}}>Total</td>
                <td style={{...st.td,fontWeight:700,color:D.danger}}>{fmt(totalExp)}</td>
              </tr></tfoot>
            </table></div>
          </div>
        </>
      )}

      {reportType==="payroll" && (
        <>
          <div style={{...st.card,marginBottom:16}}>
            <div style={{...st.h2,marginBottom:12}}>Payroll Summary — {periodLabel}</div>
            <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead><tr>{["Employee","Role","Hours","Rate","Gross Pay","Entries"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {Object.entries(payByEmp).sort((a,b)=>b[1].gross-a[1].gross).map(([name,d]) => {
                  const emp = employees.find(e=>e.name===name);
                  return (
                    <tr key={name}>
                      <td style={{...st.td,fontWeight:600}}>{name}</td>
                      <td style={{...st.td,color:D.muted,fontSize:12}}>{emp?.role||"—"}</td>
                      <td style={st.td}>{d.hours.toFixed(2)}h</td>
                      <td style={{...st.td,color:D.muted}}>{emp?fmt(emp.rate)+"/hr":"—"}</td>
                      <td style={{...st.td,fontWeight:600,color:D.warning}}>{fmt(d.gross)}</td>
                      <td style={{...st.td,color:D.muted}}>{d.entries}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr>
                <td colSpan={4} style={{...st.td,fontWeight:700}}>Total Payroll</td>
                <td style={{...st.td,fontWeight:700,color:D.warning}}>{fmt(totalPayroll)}</td>
                <td style={st.td}></td>
              </tr></tfoot>
            </table></div>
          </div>
          <div style={st.card}>
            <div style={{...st.h2,marginBottom:12}}>Pay Entry Detail</div>
            <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead><tr>{["Date","Employee","Type","Hours","Rate","Amount","Status"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {payEntries.slice().sort((a,b)=>(a.date||"").localeCompare(b.date||"")).map(e => {
                  const emp = employees.find(x=>x.id===e.user_id);
                  const amt = e.pay_type==="Hourly"||e.pay_type==="Overtime" ? (Number(e.hours)||0)*(Number(e.rate)||0) : Number(e.rate)||0;
                  return (
                    <tr key={e.id}>
                      <td style={st.td}>{fmtDate(e.date)}</td>
                      <td style={{...st.td,fontWeight:500}}>{emp?.name||"Unknown"}</td>
                      <td style={{...st.td,fontSize:11,color:D.muted}}>{e.pay_type}</td>
                      <td style={st.td}>{e.pay_type==="Hourly"||e.pay_type==="Overtime"?e.hours+"h":"—"}</td>
                      <td style={{...st.td,color:D.muted}}>{e.rate?fmt(e.rate):"—"}</td>
                      <td style={{...st.td,fontWeight:500,color:D.warning}}>{fmt(amt)}</td>
                      <td style={st.td}><span style={{fontSize:11,color:e.status==="Paid"?D.success:D.warning}}>{e.status||"Pending"}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        </>
      )}

      {reportType==="ar" && (
        <>
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:16}}>
            {[
              {label:"Current",val:aging.current,color:D.success},
              {label:"1–30 days",val:aging.d30,color:D.warning},
              {label:"31–60 days",val:aging.d60,color:"#f0a030"},
              {label:"61–90 days",val:aging.d90,color:D.danger},
              {label:"90+ days",val:aging.over90,color:D.danger},
            ].map(b => (
              <div key={b.label} style={{...st.card,textAlign:"center",padding:"14px 10px"}}>
                <div style={{fontSize:11,color:D.muted,marginBottom:6}}>{b.label}</div>
                <div style={{fontSize:18,fontWeight:700,color:b.val>0?b.color:D.muted}}>{fmt(b.val)}</div>
              </div>
            ))}
          </div>
          <div style={st.card}>
            <div style={{...st.h2,marginBottom:12}}>Outstanding Invoices</div>
            <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead><tr>{["Invoice #","Client","Date","Due Date","Days Overdue","Amount","Status"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {agingRows.slice().sort((a,b)=>b.daysOverdue-a.daysOverdue).map(i => (
                  <tr key={i.id}>
                    <td style={{...st.td,color:D.accent}}>{i.number||"—"}</td>
                    <td style={{...st.td,fontWeight:500}}>{clientName(i.clientId)}</td>
                    <td style={st.td}>{fmtDate(i.date)}</td>
                    <td style={st.td}>{fmtDate(i.dueDate)}</td>
                    <td style={{...st.td,fontWeight:600,color:i.daysOverdue>60?D.danger:i.daysOverdue>30?D.warning:D.success}}>{i.daysOverdue>0?i.daysOverdue+" days":"Current"}</td>
                    <td style={{...st.td,fontWeight:600,color:D.text}}>{fmt(i.amount)}</td>
                    <td style={st.td}><span style={{fontSize:11,color:i.status==="Overdue"?D.danger:D.warning}}>{i.status}</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td colSpan={5} style={{...st.td,fontWeight:700}}>Total Outstanding</td>
                <td style={{...st.td,fontWeight:700,color:D.warning}}>{fmt(outstanding)}</td>
                <td style={st.td}></td>
              </tr></tfoot>
            </table></div>
          </div>
        </>
      )}

      {reportType==="tax" && (
        <>
          <div style={{...st.card,border:`1px solid ${D.warning}44`,background:D.warning+"08",marginBottom:16}}>
            <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:14}}>
              <span style={{fontSize:20}}>⚠️</span>
              <div style={{fontSize:13,color:D.muted,lineHeight:1.6}}>These are rough estimates for planning purposes only, based on simplified federal rates. They do not account for state taxes, deductions, credits, depreciation, filing status, or your specific circumstances. <strong style={{color:D.warning}}>Always consult a licensed CPA before filing.</strong></div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <div style={st.card}>
              <div style={{...st.h2,marginBottom:14}}>Income Summary — {periodLabel}</div>
              {[
                {label:"Gross Revenue",val:revenue,color:D.success},
                {label:"Total Expenses",val:-totalExp,color:D.danger},
                {label:"Payroll Paid",val:-totalPayroll,color:D.danger},
                {label:"Recurring/Overhead",val:-recurringCosts,color:D.danger},
                {label:"Net Taxable Income",val:estimatedTaxableIncome,color:estimatedTaxableIncome>=0?D.success:D.danger,bold:true},
              ].map((row,i) => (
                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${D.border}`}}>
                  <span style={{fontSize:13,color:row.bold?D.text:D.muted,fontWeight:row.bold?600:400}}>{row.label}</span>
                  <span style={{fontSize:13,fontWeight:row.bold?700:500,color:row.color}}>{row.val<0?`(${fmt(Math.abs(row.val))})`:fmt(row.val)}</span>
                </div>
              ))}
            </div>
            <div style={st.card}>
              <div style={{...st.h2,marginBottom:14}}>Estimated Tax Liability</div>
              {[
                {label:"Self-Employment Tax (15.3%)",val:seTax,note:"On 92.35% of net income"},
                {label:"Federal Income Tax (22%)",val:fedTax,note:"After SE tax deduction"},
                {label:"Total Est. Tax Liability",val:totalEstTax,bold:true},
                {label:"Quarterly Payment (÷4)",val:quarterlyPayment,note:"Apr 15 · Jun 15 · Sep 15 · Jan 15",yellow:true},
              ].map((row,i) => (
                <div key={i} style={{padding:"8px 0",borderBottom:`1px solid ${D.border}`}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{fontSize:13,color:row.bold?D.text:D.muted,fontWeight:row.bold?700:400}}>{row.label}</span>
                    <span style={{fontSize:row.bold?15:13,fontWeight:700,color:row.yellow?D.warning:D.danger}}>{fmt(row.val)}</span>
                  </div>
                  {row.note && <div style={{fontSize:10,color:D.muted,marginTop:2}}>{row.note}</div>}
                </div>
              ))}
            </div>
          </div>
          <div style={{...st.card,marginTop:0}}>
            <div style={{...st.h2,marginBottom:12}}>Common Deductions Checklist</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:13,color:D.muted}}>
              {["Vehicle mileage or actual vehicle expenses","Home office deduction (if applicable)","Tools and equipment purchased","Phone and internet (business portion)","Professional licenses and permits","Insurance premiums (liability, vehicle)","Continuing education and training","Advertising and marketing expenses","Accounting and legal fees","Retirement contributions (SEP-IRA, Solo 401k)","Health insurance premiums","Office supplies and software subscriptions"].map(d => (
                <div key={d} style={{display:"flex",gap:8,alignItems:"flex-start"}}><span style={{color:D.success,flexShrink:0}}>□</span>{d}</div>
              ))}
            </div>
            <div style={{marginTop:12,fontSize:12,color:D.muted,padding:"8px 12px",background:D.surface,borderRadius:7}}>
              Keep receipts and records for all deductions. AmpBooks captures expenses automatically — export your data and send it to your accountant.
            </div>
          </div>
        </>
      )}

      {reportType==="invoices" && (
        <div style={st.card}>
          <div style={{...st.h2,marginBottom:12}}>Invoice Ledger — {periodLabel}</div>
          <div style={{overflowX:"auto"}}><table style={st.table}>
            <thead><tr>{["Invoice #","Client","Date","Due Date","Amount","Status","Paid Date"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
            <tbody>
              {allInvoices.slice().sort((a,b)=>(a.date||"").localeCompare(b.date||"")).map(i => (
                <tr key={i.id}>
                  <td style={{...st.td,color:D.accent,fontWeight:500}}>{i.number||"—"}</td>
                  <td style={{...st.td,fontWeight:500}}>{clientName(i.clientId)}</td>
                  <td style={st.td}>{fmtDate(i.date)}</td>
                  <td style={st.td}>{fmtDate(i.dueDate)}</td>
                  <td style={{...st.td,fontWeight:600,color:D.text}}>{fmt(calcTotal(i.items))}</td>
                  <td style={st.td}><span style={{fontSize:11,fontWeight:500,padding:"2px 7px",borderRadius:5,background:i.status==="Paid"?D.success+"22":i.status==="Overdue"?D.danger+"22":D.surface,color:i.status==="Paid"?D.success:i.status==="Overdue"?D.danger:D.muted}}>{i.status}</span></td>
                  <td style={{...st.td,color:D.muted}}>{i.paidDate?fmtDate(i.paidDate):"—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td colSpan={4} style={{...st.td,fontWeight:700}}>Total</td>
              <td style={{...st.td,fontWeight:700,color:D.text}}>{fmt(allInvoices.reduce((s,i)=>s+calcTotal(i.items),0))}</td>
              <td colSpan={2} style={{...st.td,color:D.muted,fontSize:12}}>Paid: {fmt(revenue)} · Outstanding: {fmt(outstanding)}</td>
            </tr></tfoot>
          </table></div>
        </div>
      )}

      {reportType==="bidaccuracy" && (
        <div style={st.card}>
          <div style={{...st.h2,marginBottom:8}}>Estimated vs Actual Hours</div>
          <div style={{fontSize:12,color:D.muted,marginBottom:16,maxWidth:560}}>
            Compares each completed job's bid (estimated labor hours) against actual hours logged in Payroll for that job. Only jobs linked to a won bid and marked Completed show up here.
          </div>
          {!bidAccuracy ? (
            <div style={{color:D.muted,fontSize:13}}>Loading...</div>
          ) : bidAccuracy.jobs.length === 0 ? (
            <div style={{color:D.muted,fontSize:13,padding:"20px 0",textAlign:"center"}}>
              No completed jobs linked to a won bid yet. Link a job to its bid (in the Jobs page) and mark it Completed once finished to start tracking accuracy here.
            </div>
          ) : (
            <>
              {bidAccuracy.calibration && (
                <div style={{marginBottom:14,padding:"10px 14px",borderRadius:8,fontSize:13,background:D.surface,border:`1px solid ${D.border}`}}>
                  {bidAccuracy.calibration.confident ? (
                    <>
                      <strong style={{color:D.text}}>Calibration factor: {bidAccuracy.calibration.factor}×</strong>
                      <span style={{color:D.muted}}> — based on {bidAccuracy.calibration.sampleSize} completed jobs, actual hours have run {bidAccuracy.calibration.factor>1?`${Math.round((bidAccuracy.calibration.factor-1)*100)}% over`:bidAccuracy.calibration.factor<1?`${Math.round((1-bidAccuracy.calibration.factor)*100)}% under`:"on target with"} estimate on average. Consider this when pricing new bids.</span>
                    </>
                  ) : (
                    <span style={{color:D.muted}}>Not enough completed jobs yet to calculate a reliable calibration factor ({bidAccuracy.calibration.sampleSize}/{bidAccuracy.calibration.minSamples} minimum).</span>
                  )}
                </div>
              )}
              <div style={{overflowX:"auto"}}><table style={st.table}>
                <thead><tr>{["Job","Bid #","Date","Est. Hours","Actual Hours","Δ Hours"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {bidAccuracy.jobs.map(r => (
                    <tr key={r.jobId}>
                      <td style={st.td}>{r.jobTitle}</td>
                      <td style={{...st.td,color:D.accent,fontWeight:500}}>{r.bidNumber||"—"}</td>
                      <td style={st.td}>{fmtDate(r.jobDate)}</td>
                      <td style={st.td}>{r.estimatedHours}</td>
                      <td style={st.td}>{r.actualHours}</td>
                      <td style={{...st.td,fontWeight:600,color:r.delta>0?D.danger:r.delta<0?D.success:D.text}}>{r.delta>0?"+":""}{r.delta}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
